/**
 * en / fr / es strings for the review step (`UploadValidation.tsx`) and the error texts set by
 * `BulkUploadHelpers.tsx` → `mapErrorMessage`. `InvalidInputWrapper` compares a row's message with
 * `errorMessages.partNumber` and `errorMessages.multipleParts` to decide whether the part-number
 * cell becomes a search, so those two values are matched as well as shown.
 *
 * Not read by any file: `addItems`, `itemUnfound`, `noQuantity`, `isIncorrect`, `qtyUom`. The `es`
 * value of `OK` is "Neutral".
 */
import { LanguageContent } from "@ryerson/frontend.common";

/** Shape of each language's strings in {@link UploadValidationContent}. */
export interface UploadValidationText {
	unableToProcess: string;
	pleaseReview: string;
	processCorrections: string;
	itemsAreReady: string;
	shoppingCart: string;
	shoppingList: string;
	itemIs: string;
	itemsAre: string;
	addItems: string;
	errorResolved: string;
	itemUnfound: string;
	noQuantity: string;
	isIncorrect: string;
	qtyUom: string;
	OK: string;
	quote: string;
	headers: {
		partNumber: string;
		quantity: string;
		quantityUom: string;
		pricingUom: string;
		confirmation: string;
		errorMessage: string;
		trash: string;
	};
	optional: string;
	errorMessages: {
		partNumber: string;
		quantity: string;
		quantityUom: string;
		pricingUom: string;
		multipleParts: string;
	};
}

/** The review step's strings by language. */
export const UploadValidationContent: LanguageContent<UploadValidationText> = {
	en: {
		quote: "Quote",
		unableToProcess: "We were unable to process %itemNumber% item",
		pleaseReview: ". Please review and fix the following errors.",
		processCorrections: "Process Corrections & Try Again",
		itemsAreReady: "%readyItems% %itemsAre% ready to be added to the %ShoppingCart%",
		shoppingCart: "shopping cart",
		shoppingList: "shopping list",

		itemIs: "Item is",
		itemsAre: "Items are",
		addItems: "Add %readyItems% Items to the Shopping List",
		errorResolved: "Error Resolved",
		itemUnfound: "Item not found",
		noQuantity: "Item without quantity",
		isIncorrect: "is incorrect",
		qtyUom: "Qty UOM",
		OK: "OK",
		headers: {
			partNumber: "Product",
			quantity: "Quantity",
			quantityUom: "Quantity UOM",
			pricingUom: "Price UOM",
			confirmation: "Confirmation",
			errorMessage: "Error",
			trash: "",
		},
		optional: "(optional)",

		errorMessages: {
			partNumber: "Part not found",
			quantity: "Qty is not valid",
			quantityUom: "Qty uom is not valid",
			pricingUom: "Price uom is not valid",
			multipleParts: "Multiple parts found",
		},
	},
	fr: {
		quote: "Devis",
		unableToProcess: "Nous n’avons pas pu traiter %itemNumber% des article",
		pleaseReview: ". Veuillez revoir et corriger les erreurs suivantes.",
		processCorrections: "Apportez les corrections nécessaires et essayez de nouveau.",
		itemsAreReady: "%readyItems% %itemsAre% prêts à être ajoutés %ShoppingCart%",
		shoppingCart: "Panier",
		shoppingList: "Liste d’achats",
		itemIs: "article est",
		itemsAre: "articles sont",
		addItems: "Ajouter %readyItems% articles à la liste d’achats",
		errorResolved: "Erreur résolue",
		itemUnfound: "Article non trouvé",
		noQuantity: "Article sans quantité",
		isIncorrect: "non valide",
		qtyUom: "Quantité (UdM)",
		OK: "OK",
		headers: {
			partNumber: "Produit",
			quantity: "Quantité",
			quantityUom: "Quantité (UdM)",
			pricingUom: "Prix (UdM)",
			confirmation: "Confirmation",
			errorMessage: "Erreur",
			trash: "",
		},
		optional: "(facultatif)",
		errorMessages: {
			partNumber: "Pièce introuvable",
			quantity: "La quantité n’est pas valide.",
			quantityUom: "La quantité (UdM) n’est pas valide.",
			pricingUom: "Le prix (UdM) n’est pas valide.",
			multipleParts: "Plusieurs pièces trouvées",
		},
	},
	es: {
		quote: "Cotizar",
		unableToProcess: "No hemos podido procesar %itemNumber% articulo",
		pleaseReview: ". Pro favor revise y corrija los errores siguintes.",
		processCorrections: "Procesar correcciones y volver a intentar",
		itemsAreReady: "%readyItems% %itemsAre% listos par ser agregados %ShoppingCart%",
		shoppingCart: "Carrito de compras",
		shoppingList: "Lista de compra",
		itemIs: "articulo esta",
		itemsAre: "articulos estan",
		addItems: "Agregar %readyItems% artículos a la Lista de compras",
		errorResolved: "Error resuelto",
		itemUnfound: "Artículo no encontrado",
		noQuantity: "Artículo sin cantidad",
		isIncorrect: "es incorrecto",
		qtyUom: "UDM por cantidad",
		OK: "Neutral",
		headers: {
			partNumber: "Producto",
			quantity: "Cantidad",
			quantityUom: "UDM por cantidad",
			pricingUom: "UOM por precio",
			confirmation: "Confirmación",
			errorMessage: "Error",
			trash: "",
		},
		optional: "(opcional)",
		errorMessages: {
			partNumber: "Pieza no encontrada",
			quantity: "La cantidad no es válida",
			quantityUom: "La UDM por cantidad no es válida",
			pricingUom: "La UDM por precio no es válida",
			multipleParts: "Se encontraron varias piezas",
		},
	},
};
